/**
 * Atrium brand mark — an architectural arch/skylight motif that nods to the
 * "open atrium" concept while staying readable at any size. Built as an inline
 * SVG so it's crisp on retina and color-customisable via the `accent` prop
 * without shipping multiple PNG assets.
 *
 * Variants:
 *  - 'mark'   → just the geometric mark (square, no text). Use in tight slots
 *               like nav headers, alongside the property logo tile.
 *  - 'inline' → mark + wordmark side-by-side. Default. Use in login/footer.
 *  - 'stack'  → mark above wordmark. Use as a hero brand block.
 */
const ATRIUM_VARIANTS = ['mark', 'inline', 'stack'];

export default function AtriumLogo({
  variant = 'inline',
  size = 32,
  accent = '#1F4FBE',
  text = '#1A2E2E',
  showTagline = false,
  className = '',
  testId = 'atrium-logo',
}) {
  const v = ATRIUM_VARIANTS.includes(variant) ? variant : 'inline';
  // Brand mark — a wide arch resting on two pillars, with a sun-disc
  // skylight in the keystone position. Two-tone: pillars/arch in `accent`,
  // skylight + ground in deeper text color so the mark doesn't disappear on
  // teal backgrounds.
  const Mark = (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Atrium"
      style={{ display: 'block', flexShrink: 0 }}
    >
      {/* Soft circular wash behind the arch — gives the mark a coin-like
          presence on dark and light backgrounds without needing a separate
          card container. */}
      <circle cx="32" cy="32" r="30" fill={accent} opacity="0.08" />
      {/* Ground line */}
      <rect x="10" y="49" width="44" height="3" rx="1.5" fill={accent} />
      {/* Left pillar */}
      <rect x="13" y="26" width="6" height="23" rx="1.5" fill={accent} />
      {/* Right pillar */}
      <rect x="45" y="26" width="6" height="23" rx="1.5" fill={accent} />
      {/* Arch — a half-ring spanning the pillars, drawn as a stroked path so
          the inner cutout is clean on any background. */}
      <path
        d="M16 28 A 16 16 0 0 1 48 28"
        fill="none"
        stroke={accent}
        strokeWidth="6"
        strokeLinecap="round"
      />
      {/* Skylight disc at the keystone */}
      <circle cx="32" cy="22" r="4" fill={text} />
      {/* Skylight rays */}
      <g stroke={text} strokeWidth="1.5" strokeLinecap="round" opacity="0.85">
        <line x1="32" y1="11" x2="32" y2="14" />
        <line x1="22" y1="14" x2="24" y2="16" />
        <line x1="42" y1="14" x2="40" y2="16" />
      </g>
    </svg>
  );

  if (v === 'mark') {
    return <span data-testid={testId} className={className}>{Mark}</span>;
  }

  // Wordmark — Cormorant Garamond (already loaded by the app) for editorial
  // hospitality feel. Letter-spacing pulled tight so the word reads as a
  // single object next to the mark.
  const fontSize = Math.round(size * 0.85);
  const Wordmark = (
    <span
      style={{
        fontFamily: "'Cormorant Garamond', serif",
        fontWeight: 500,
        fontSize: `${fontSize}px`,
        color: text,
        letterSpacing: '-0.01em',
        lineHeight: 1,
      }}
    >
      Atrium
    </span>
  );

  const Tagline = showTagline && (
    <span
      style={{
        fontFamily: 'Manrope, sans-serif',
        fontWeight: 700,
        fontSize: `${Math.max(8, Math.round(size * 0.28))}px`,
        color: '#7A8C8C',
        letterSpacing: '0.25em',
        textTransform: 'uppercase',
        marginTop: v === 'stack' ? 6 : 0,
      }}
    >
      Hotel Transformations
    </span>
  );

  if (v === 'stack') {
    return (
      <span
        data-testid={testId}
        className={className}
        style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}
      >
        {Mark}
        {Wordmark}
        {Tagline}
      </span>
    );
  }

  // inline (default)
  return (
    <span
      data-testid={testId}
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap: Math.max(6, Math.round(size * 0.28)) }}
    >
      {Mark}
      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
        {Wordmark}
        {Tagline}
      </span>
    </span>
  );
}
